/**
 * Helpers for trip transportation segments (flights, trains, buses, ferries).
 *
 * Times are local wall-clock strings ("YYYY-MM-DDTHH:MM") plus optional UTC
 * offsets in minutes at each end. The traveler-facing rule: always DISPLAY
 * local times, always COMPUTE duration through the offsets — a westbound
 * LAX→JFK-style flight looks like a 30-minute trip if you subtract wall
 * clocks, and eastbound looks absurdly long.
 */

import i18n from '../i18n';

export const TRANSPORT_MODES = [
  { value: 'flight', get label() { return i18n.t('labels:transport.modes.flight'); }, icon: 'airplane' },
  { value: 'train', get label() { return i18n.t('labels:transport.modes.train'); }, icon: 'train' },
  { value: 'bus', get label() { return i18n.t('labels:transport.modes.bus'); }, icon: 'bus' },
  { value: 'ferry', get label() { return i18n.t('labels:transport.modes.ferry'); }, icon: 'ferry' },
  // Car is a first-class answer, not a failure to sell a ticket
  // (GETTING_AROUND_PLAN): recording a drive resolves travel-day gaps the
  // same way a train does. Taxi is its own mode — someone else drives, so
  // it's a booked ride (confirmation, cost) rather than a car you park.
  { value: 'car', get label() { return i18n.t('labels:transport.modes.car'); }, icon: 'car' },
  { value: 'taxi', get label() { return i18n.t('labels:transport.modes.taxi'); }, icon: 'taxi' },
  { value: 'other', get label() { return i18n.t('labels:transport.modes.other'); }, icon: 'mapPin' },
];

/**
 * Save caps, mirroring tripsService's: a trip PUT carrying more segments, or
 * a segment with more passengers, is refused whole (never truncated). The
 * planner stops the add before it gets there.
 */
export const MAX_TRIP_TRANSPORTS = 100;
export const MAX_TRANSPORT_TRAVELERS = 30;

/** Would saving `segment` ADD a segment past the cap? Edits never count. */
export const addsTransportOverLimit = (transports, segment) =>
  (transports || []).length >= MAX_TRIP_TRANSPORTS
  && !(transports || []).some(t => t.clientUid && t.clientUid === segment?.clientUid);

/** Modes bookable via the ground-transport (Omio) link — car/taxi are not. */
export const BOOKABLE_GROUND_MODES = new Set(['train', 'bus', 'ferry']);

/** Modes where a drive-time estimate applies. */
export const DRIVE_MODES = new Set(['car', 'taxi']);

/**
 * Modes where a CARRIER issues you a ticket — the only ones with a seat, a
 * checked-bag allowance, a service number, or a station/airport code.
 *
 * `other` is deliberately excluded. It is the catch-all (hotel shuttle,
 * rideshare, cable car, bike), and inheriting the airline-shaped fields gave
 * it a 3-character carrier code, a seat and a bag counter that describe
 * nothing. Same reasoning that keeps them off car/taxi — see the baggage note
 * in TransportTravelers.
 */
export const TICKETED_MODES = new Set(['flight', 'train', 'bus', 'ferry']);

/**
 * What the boarding point is CALLED for this mode — planes have terminals,
 * trains have platforms, coaches have stands. One stored column
 * (`origin_terminal`) serves all three; only the noun differs. Null for modes
 * with no boarding point at all (car/taxi/other), which suppresses the chip.
 */
const BOARDING_NOUN_BY_MODE = {
  flight: 'terminal',
  ferry: 'terminal',
  train: 'platform',
  bus: 'stand',
};
export const boardingNoun = (mode) => BOARDING_NOUN_BY_MODE[mode] || null;

export const transportModeMeta = (mode) =>
  TRANSPORT_MODES.find(m => m.value === mode) || TRANSPORT_MODES[TRANSPORT_MODES.length - 1];

/** "2026-11-14T08:05" → { date: "2026-11-14", time: "08:05" } */
export const splitLocalDateTime = (local) => {
  if (typeof local !== 'string' || local.length < 16) return { date: '', time: '' };
  return { date: local.slice(0, 10), time: local.slice(11, 16) };
};

/** ("2026-11-14", "08:05") → "2026-11-14T08:05", or null if either missing */
export const joinLocalDateTime = (date, time) =>
  (date && time ? `${date}T${time}` : null);

/**
 * True duration in minutes between departure and arrival.
 *
 * With both offsets present:  (arrival_utc - departure_utc).
 * Without offsets (manual train/bus entries): the naive wall-clock difference,
 * which is correct when both ends share a timezone — the common case for
 * ground transport. Returns null when either datetime is missing or the
 * result is non-positive (bad data beats a "-3h" chip).
 */
export function transportDurationMinutes(t) {
  if (!t?.departureLocal || !t?.arrivalLocal) return null;
  // Parse both as if UTC purely to difference the wall clocks; the offset
  // correction below converts that into a real elapsed duration.
  const dep = Date.parse(`${t.departureLocal}:00Z`);
  const arr = Date.parse(`${t.arrivalLocal}:00Z`);
  if (Number.isNaN(dep) || Number.isNaN(arr)) return null;
  let mins = Math.round((arr - dep) / 60000);
  if (Number.isInteger(t.departureOffsetMin) && Number.isInteger(t.arrivalOffsetMin)) {
    mins += t.departureOffsetMin - t.arrivalOffsetMin;
  }
  return mins > 0 ? mins : null;
}

/**
 * Shift a naive local "YYYY-MM-DDTHH:MM" by a signed minute delta of REAL
 * elapsed time, returning a new local string (day/month/year boundaries
 * handled). '' on bad input. Used for "leave home by X" = flight departure −
 * buffer − drive.
 *
 * DST: the string is interpreted in the BROWSER's timezone (component-wise
 * Date constructor), shifted by timestamp arithmetic, and read back as local
 * components — so a buffer window that crosses a DST switch yields the
 * wall-clock giving the full elapsed lead time (a 05:00 spring-forward-night
 * flight with 3h of lead → "leave by 01:00", not the naive "02:00" that is
 * secretly only 2 real hours). This is exact whenever the viewer's browser
 * shares the wall-clock's timezone — the normal case for a leave-HOME line —
 * and no worse than naive arithmetic otherwise. (True cross-zone correctness
 * needs an IANA zone for the airport, which transports don't carry.)
 */
export function shiftLocalMinutes(local, deltaMin) {
  if (typeof local !== 'string' || local.length < 16 || !Number.isFinite(deltaMin)) return '';
  const y = +local.slice(0, 4);
  const mo = +local.slice(5, 7);
  const d = +local.slice(8, 10);
  const h = +local.slice(11, 13);
  const mi = +local.slice(14, 16);
  if ([y, mo, d, h, mi].some(Number.isNaN)) return '';
  const base = new Date(y, mo - 1, d, h, mi).getTime();
  if (Number.isNaN(base)) return '';
  const s = new Date(base + deltaMin * 60000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}T${pad(s.getHours())}:${pad(s.getMinutes())}`;
}

/**
 * Airport check-in lead time for "leave home by X". Flight DURATION is a cheap
 * proxy for domestic vs international/long-haul (a precise country/Schengen
 * check is a later refinement): a short hop needs less lead time than a
 * long-haul departure. Minutes.
 */
export function airportCheckInBufferMin(transport) {
  const dur = transportDurationMinutes(transport);
  if (!dur) return 120;      // unknown → a safe middle default
  if (dur < 150) return 90;  // short-haul / likely domestic
  return 180;                // long-haul / likely international (~3h)
}

/** 935 → "15h 35m", 45 → "45m", 120 → "2h" */
export function formatDurationShort(mins) {
  if (!Number.isInteger(mins) || mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return i18n.t('labels:transport.duration.minutesOnly', { m });
  return m === 0
    ? i18n.t('labels:transport.duration.hoursOnly', { h })
    : i18n.t('labels:transport.duration.hoursMinutes', { h, m });
}

/**
 * Arrival DATE for a ground ride, given the departure date and wall-clock
 * times at both ends: the departure date, rolled one day forward when the
 * arrival clock reads earlier than the departure clock. Schedule feeds
 * (iRail connections) carry times without dates, so a past-midnight
 * arrival is inferred from the clock ordering — sound for rides under 24h,
 * which ground transport is. Missing/ambiguous inputs (equal clocks
 * included) fall back to the departure date.
 */
export function rideArrivalDate(departureDate, departureTime, arrivalTime) {
  if (!departureDate) return '';
  if (!departureTime || !arrivalTime || arrivalTime >= departureTime) return departureDate;
  const ms = Date.parse(`${departureDate}T00:00:00Z`);
  if (Number.isNaN(ms)) return departureDate;
  return new Date(ms + 86400000).toISOString().slice(0, 10);
}

/**
 * The mirror of rideArrivalDate, for arrive-by scheduling where the known
 * date is the ARRIVAL day: the departure date is the arrival date, rolled
 * one day back when the clock ordering shows the ride left the previous
 * evening. Same fallback rules.
 */
export function rideDepartureDate(arrivalDate, departureTime, arrivalTime) {
  if (!arrivalDate) return '';
  if (!departureTime || !arrivalTime || arrivalTime >= departureTime) return arrivalDate;
  const ms = Date.parse(`${arrivalDate}T00:00:00Z`);
  if (Number.isNaN(ms)) return arrivalDate;
  return new Date(ms - 86400000).toISOString().slice(0, 10);
}

/**
 * Calendar days between the local departure and arrival dates — the "+1" /
 * "+2" badge airlines print next to overnight arrival times. 0 = same day.
 */
export function arrivalDayOffset(t) {
  if (!t?.departureLocal || !t?.arrivalLocal) return 0;
  const dep = Date.parse(`${t.departureLocal.slice(0, 10)}T00:00:00Z`);
  const arr = Date.parse(`${t.arrivalLocal.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(dep) || Number.isNaN(arr)) return 0;
  return Math.round((arr - dep) / 86400000);
}

/**
 * Does this segment touch this trip-day in wall-clock terms? The departure
 * day always. When the arrival wall-clock is after the departure (normal
 * overnight / multi-day spans), every day through the arrival date too — so
 * a red-eye shows on BOTH its departure and arrival days. A non-positive
 * wall-clock span (westbound dateline crossings can "arrive before departing")
 * or missing arrival falls back to departure-day only.
 */
export function transportTouchesDate(t, date) {
  if (!t?.departureLocal || !date) return false;
  const depDate = t.departureLocal.slice(0, 10);
  const arrDate = t.arrivalLocal && t.arrivalLocal > t.departureLocal
    ? t.arrivalLocal.slice(0, 10)
    : null;
  if (!arrDate) return date === depDate;
  return date >= depDate && date <= arrDate;
}

/**
 * Which role a segment plays on a trip-day: 'departure' on its departure
 * date, 'arrival' on the wall-clock arrival date, 'transit' for full days
 * spent en route (3+-day spans), null when the segment doesn't touch the day.
 * Built ON transportTouchesDate so its edge rules are inherited, not
 * re-derived: a non-positive wall span (westbound dateline "arrive before
 * departing") or missing arrival is departure-day only — such a segment can
 * never report 'arrival' or 'transit'. Views must classify through this
 * helper rather than comparing date slices themselves.
 */
export function transportDayRole(t, date) {
  if (!transportTouchesDate(t, date)) return null;
  if (t.departureLocal.slice(0, 10) === date) return 'departure';
  if (t.arrivalLocal.slice(0, 10) === date) return 'arrival';
  return 'transit';
}

/** Sum of checked bags across a segment's travelers. */
export const totalCheckedBags = (t) =>
  (t?.travelers || []).reduce((sum, tv) => sum + (tv.checkedBags || 0), 0);

/**
 * Stable segment identity across renders and refetches: the client uid first
 * (it survives the save round-trip; a just-added segment has no server id
 * until the next fetch), then the server row id. The same precedence the day
 * card uses for React keys — status maps keyed through this helper stay in
 * sync with the rows they describe.
 */
export const transportKey = (t) => t?.clientUid || t?.id;

// A same-airport gap up to this long is a layover you wait out airside — no
// ground transfer. Beyond it (an overnight or long self-transfer) a hotel/ride
// is legitimate, so both ends keep their nudge. 8h is the practical
// layover/stopover line for connections you don't leave the terminal for.
const MAX_LAYOVER_MIN = 8 * 60;

/**
 * Two DIFFERENT airports this close apart are the same metro area, so a
 * connection between them is a self-transfer you cross town for, not two
 * unrelated journeys: JFK↔LGA 16km, ORD↔MDW 26km, JFK↔EWR 34km, MXP↔LIN 45km,
 * LHR↔LGW 72km, NRT↔HND 75km. 100km clears all of them without reaching the
 * next city. Co-located airports also share a timezone, which is what makes
 * the wall-clock gap trustworthy without offsets (see connectionGap).
 */
const TRANSFER_MAX_KM = 100;

// Gaps below these read as tight enough to mention. Not errors — a heads-up,
// so the traveler decides. Airside is the baseline; a terminal change costs
// a train/bus ride inside security; a cross-town airport transfer needs real
// ground time (JFK→LGA is ~45-60min before traffic) plus a second check-in.
const TIGHT_AIRSIDE_MIN = 60;
const TIGHT_TERMINAL_CHANGE_MIN = 90;
const TIGHT_TRANSFER_MIN = 180;

/** A flight-transfer nudge end, as keyed for the suppression set below. */
const connectionEndKey = (t, direction) => `${transportKey(t)}:${direction}`;

/** Great-circle km between two {lat,lng}; null when either is missing. */
export function coordDistanceKm(a, b) {
  if (!a || !b || !Number.isFinite(a.lat) || !Number.isFinite(a.lng)
    || !Number.isFinite(b.lat) || !Number.isFinite(b.lng)) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * How one traveler is identified for connection matching. Prefers the linked
 * account id; falls back to the normalized display name, because a traveler
 * outside the trip roster is stored as free text only (tripsService clamps an
 * unknown user_id to NULL) and would otherwise match nobody.
 */
const travelerIdentity = (tv) =>
  tv?.userId || String(tv?.displayName || '').trim().toLowerCase();

const travelerIdentitySet = (t) =>
  new Set((t?.travelers || []).map(travelerIdentity).filter(Boolean));

/**
 * Do these two flights carry a common passenger? Overlap, not equality: on a
 * family trip a parent may connect onward while a child's leg is tagged
 * separately, and the tagged sets differ in size and order.
 *
 * An UNTAGGED flight matches everything. Tagging travelers is optional and
 * most segments have none, so treating "untagged" as "unknown, assume yours"
 * keeps the common single-party itinerary working — the same permissive
 * default segmentCoverage uses when a segment has no travelers. Only two
 * flights that are BOTH tagged and share nobody are held apart, which is the
 * false pair this rule exists to break: two people routing through the same
 * airport within 8h on unrelated flights are not each other's layover.
 */
function sharesTraveler(a, b) {
  const ka = travelerIdentitySet(a);
  if (ka.size === 0) return true;
  const kb = travelerIdentitySet(b);
  if (kb.size === 0) return true;
  for (const k of ka) if (kb.has(k)) return true;
  return false;
}

/**
 * Real minutes waiting between landing on `a` and departing on `b`, or null
 * when we can't know. Times are naive local wall clocks with a NULLABLE UTC
 * offset (populated only by the flight lookup — manual entry leaves it unset),
 * and airports carry no IANA zone, so there are two honest paths:
 *
 *   1. Both relevant offsets present → exact UTC arithmetic, any zone. Also
 *      the only path that survives a layover spanning a DST switch.
 *   2. Offsets missing but the two ends share a timezone → the naive
 *      wall-clock difference IS the elapsed time.
 *
 * Callers only pair co-located airports, so (2) always holds as a fallback
 * here; the guard stays explicit so a future caller that pairs distant
 * airports gets null rather than a confidently wrong number.
 */
function connectionGapMinutes(a, b, sameZone) {
  const arr = Date.parse(`${a.arrivalLocal}:00Z`);
  const dep = Date.parse(`${b.departureLocal}:00Z`);
  if (Number.isNaN(arr) || Number.isNaN(dep)) return null;
  const naive = Math.round((dep - arr) / 60000);
  // local = utc + offset, so the elapsed gap corrects by (earlier - later),
  // the same direction transportDurationMinutes applies within one segment.
  if (Number.isInteger(a.arrivalOffsetMin) && Number.isInteger(b.departureOffsetMin)) {
    return naive + a.arrivalOffsetMin - b.departureOffsetMin;
  }
  return sameZone ? naive : null;
}

/**
 * Every connection in an itinerary: the pairs of flights where one leg feeds
 * the next, plus the nudge ends those pairs make meaningless.
 *
 * TWO KINDS, and the difference decides whether a ground leg is still owed:
 *
 * - 'airside' — same airport (A.destinationCode === B.originCode). You wait in
 *   the terminal. Both ends' ground-transfer nudges are suppressed: Jackson
 *   Hole → Chicago → Brussels is two `trip_transports` rows, and without this
 *   Chicago draws a "drive from ORD" AND a "drive to ORD" for an airport you
 *   never exit.
 * - 'transfer' — different airports within TRANSFER_MAX_KM, the NYC/Chicago
 *   self-transfer (JFK→LGA, ORD→MDW). You DO have to cross town, so this
 *   suppresses NOTHING; the nudges on both ends are the whole point. It earns
 *   a layover row so the traveler sees the gap they have to do it in.
 *
 * Either kind requires, in addition: both legs `mode === 'flight'` with both
 * stamps and both codes present, a shared passenger (see sharesTraveler), and
 * a knowable gap in [0, MAX_LAYOVER_MIN].
 *
 * Pairing is greedy on the shortest gap, and each leg takes at most one
 * connection in and one out — so a hub with several same-day departures binds
 * to the plausible onward flight instead of every candidate at once. Chains of
 * any length still fall out naturally (JAC→ORD→BRU→AMS yields two layovers,
 * each interior airport suppressed on both sides).
 *
 * Returns { connections, ends }. `ends` is the Set of
 * "<transportKey>:<direction>" strings to suppress, airside pairs only — the
 * same contract isConnectionEnd and needsGroundLeg have always read. Pure: no
 * dependence on render order, dates, or the current day.
 */
export function computeFlightConnections(transports) {
  const flights = (transports || []).filter(
    t => t?.mode === 'flight' && t.arrivalLocal && t.departureLocal
  );
  const candidates = [];
  for (const a of flights) {
    const aCode = String(a.destinationCode || '').trim().toUpperCase();
    if (!aCode) continue;
    for (const b of flights) {
      if (b === a) continue;
      const bCode = String(b.originCode || '').trim().toUpperCase();
      if (!bCode) continue;
      const sameAirport = bCode === aCode;
      // A different code only chains when the two airports are demonstrably
      // co-located. No coordinates (airport missing from the `airports`
      // seed) ⇒ no evidence ⇒ no pair, rather than a guess off the codes.
      const km = sameAirport
        ? 0
        : coordDistanceKm(a.destinationCoordinates, b.originCoordinates);
      if (km === null || km > TRANSFER_MAX_KM) continue;
      // Forward progress required: a same-day out-and-back (ANR→LHR then
      // LHR→ANR, ≤8h apart) is a DAY TRIP, not a layover — pairing it renders
      // a bogus layover row AND suppresses both destination-side airport-run
      // prompts. Real multi-day A→B→A returns are already excluded by the
      // MAX_LAYOVER_MIN gap. Codes-only check (not gated on sameAirport) so
      // the co-located-airport variant (out via LHR, back via LGW) is caught
      // too; both codes must be non-empty so missing data never rejects.
      const aOrigin = String(a.originCode || '').trim().toUpperCase();
      const bDest = String(b.destinationCode || '').trim().toUpperCase();
      if (aOrigin && bDest && bDest === aOrigin) continue;
      if (!sharesTraveler(a, b)) continue;
      const gap = connectionGapMinutes(a, b, true);
      if (gap === null || gap < 0 || gap > MAX_LAYOVER_MIN) continue;
      candidates.push({ a, b, gap, sameAirport, aCode, bCode });
    }
  }

  // Shortest gap wins the pairing; ties break on a stable key so the result
  // never depends on input order.
  candidates.sort((x, y) => x.gap - y.gap
    || String(transportKey(x.a)).localeCompare(String(transportKey(y.a))));

  const ends = new Set();
  const connections = [];
  const takenOut = new Set();
  const takenIn = new Set();
  for (const c of candidates) {
    const outKey = transportKey(c.a);
    const inKey = transportKey(c.b);
    if (takenOut.has(outKey) || takenIn.has(inKey)) continue;
    takenOut.add(outKey);
    takenIn.add(inKey);

    const terminalFrom = String(c.a.destinationTerminal || '').trim();
    const terminalTo = String(c.b.originTerminal || '').trim();
    // Only a KNOWN change counts — a blank terminal on either side is missing
    // data, and "Terminal 5 → ?" would invent a transfer that may not exist.
    const terminalChange = c.sameAirport && terminalFrom && terminalTo
      && terminalFrom.toUpperCase() !== terminalTo.toUpperCase()
      ? { from: terminalFrom, to: terminalTo }
      : null;

    const tightBelow = !c.sameAirport
      ? TIGHT_TRANSFER_MIN
      : terminalChange ? TIGHT_TERMINAL_CHANGE_MIN : TIGHT_AIRSIDE_MIN;

    connections.push({
      key: `${outKey}->${inKey}`,
      from: c.a,
      to: c.b,
      kind: c.sameAirport ? 'airside' : 'transfer',
      // The day the waiting starts — an overnight connection belongs to the
      // landing's date, with the onward departure carrying the "+1".
      date: c.a.arrivalLocal.slice(0, 10),
      arriveCode: c.aCode,
      departCode: c.bCode,
      city: cityName(c.a.destinationCity, c.a.destinationName),
      gapMinutes: c.gap,
      terminalChange,
      tight: c.gap < tightBelow,
    });

    if (c.sameAirport) {
      ends.add(connectionEndKey(c.a, 'from')); // arrival into the layover
      ends.add(connectionEndKey(c.b, 'to'));   // departure out of it
    }
  }

  connections.sort((x, y) =>
    String(x.from.arrivalLocal).localeCompare(String(y.from.arrivalLocal)));
  return { connections, ends };
}

/**
 * Just the suppression Set — the original entry point, kept for callers that
 * only gate ground-leg nudges (transportCoverage) and don't render layovers.
 */
export const computeFlightConnectionEnds = (transports) =>
  computeFlightConnections(transports).ends;

/** Connections grouped by the trip-day their layover row belongs on. */
export function groupConnectionsByDate(connections) {
  const byDate = {};
  (connections || []).forEach(c => {
    if (!c?.date) return;
    (byDate[c.date] = byDate[c.date] || []).push(c);
  });
  return byDate;
}

/** True when this flight's ground-transfer nudge end is a suppressed layover. */
export const isConnectionEnd = (connectionEnds, transport, direction) =>
  !!connectionEnds && connectionEnds.has(connectionEndKey(transport, direction));

/**
 * Which flight ends are plausibly HOME legs — the journey-boundary ends where
 * a home-based line ("leave home by X" on a departure, "drive home" on an
 * arrival) is evidence-backed rather than proximity-coincidence. The audit
 * trace: origin_drive/dest_drive used to fire on geographic proximity alone,
 * so a Brussels resident's mid-trip Paris→Rome leg showed "leave home by X"
 * (Brussels↔CDG < 300 km) and a mid-trip landing near home showed "drive
 * home" over the booked hotel. Trip shape is the missing factor.
 *
 * Rule, per traveler group (flights grouped by their first tagged traveler's
 * userId — the same `rep` the nudge resolves — untagged flights group
 * together): the chronologically FIRST flight's departure end is the journey
 * origin (leaving home), and the LAST flight's arrival end is the journey's
 * final return (arriving home). Everything between is destination travel.
 * Out-and-back round trips per person fall out exactly; the server's
 * home-airport proximity guard remains the second factor, so a one-way trip's
 * final arrival far from home still shows nothing.
 *
 * Returns a Set of the same "<transportKey>:<direction>" strings the
 * connection-end set uses; membership tested with isConnectionEnd's twin
 * below. Pure and render-order independent.
 */
export function computeHomeLegEnds(transports) {
  const ends = new Set();
  const groups = new Map();
  (transports || []).forEach(t => {
    if (t?.mode !== 'flight' || !t.departureLocal) return;
    const rep = (t.travelers || []).find(tv => tv.userId);
    const key = rep?.userId || '';
    const list = groups.get(key) || [];
    list.push(t);
    groups.set(key, list);
  });
  groups.forEach(flights => {
    const byDeparture = [...flights].sort((a, b) =>
      a.departureLocal.localeCompare(b.departureLocal));
    const byArrival = [...flights].sort((a, b) =>
      (a.arrivalLocal || a.departureLocal).localeCompare(b.arrivalLocal || b.departureLocal));
    ends.add(connectionEndKey(byDeparture[0], 'to'));                 // leaving home
    ends.add(connectionEndKey(byArrival[byArrival.length - 1], 'from')); // returning home
  });
  return ends;
}

/** True when this flight end is a journey-boundary (home-leg) end. */
export const isHomeLegEnd = (homeLegEnds, transport, direction) =>
  !!homeLegEnds && homeLegEnds.has(connectionEndKey(transport, direction));

/** "Paris (Roissy-en-France, Val-d'Oise)" → "Paris"; falls back to the raw name. */
export const cityName = (city, fallbackName) =>
  String(city || '').split('(')[0].trim() || String(fallbackName || '').trim();

/**
 * Comparison key for "is this the same city?" across the two naming worlds
 * we hold: transport endpoints ("Paris (Roissy-en-France)") and day-location
 * stops, which store full autocomplete descriptions ("Paris, France").
 * Both collapse to "paris".
 */
export const cityMatchKey = (name) =>
  String(name || '').split(',')[0].split('(')[0].trim().toLowerCase();

/**
 * Normalized "cityA|cityB" keys for every transport segment, both
 * directions. Lets a map layer tell whether a city-to-city leg is already
 * drawn as a booked segment — two dashed arcs for one journey read as
 * clutter, so the scaffolding line yields to the real one.
 */
export function transportCityLegKeys(transports) {
  const keys = new Set();
  (transports || []).forEach(t => {
    const a = cityMatchKey(cityName(t?.originCity, t?.originName));
    const b = cityMatchKey(cityName(t?.destinationCity, t?.destinationName));
    if (!a || !b || a === b) return;
    keys.add(`${a}|${b}`);
    keys.add(`${b}|${a}`);
  });
  return keys;
}

/**
 * Human destination for headlines ("Flight to London"): the airport's
 * municipality with its parenthetical suffix trimmed, then the raw
 * destination name, then the code. '' when the segment has no destination.
 */
export const transportDestinationLabel = (t) =>
  cityName(t?.destinationCity, t?.destinationName) || t?.destinationCode || '';

/**
 * Build the suggested day-location chain ("Antwerp → London") from the
 * transport segments that touch a date. Flights prefer the airport's
 * municipality (originCity/destinationCity from the airports table — CDG's
 * "Paris (Roissy-en-France, Val-d'Oise)" is trimmed to "Paris"); trains/etc.
 * fall back to the raw origin/destination names the user typed.
 *
 * Segment endpoints contribute in day order: an overnight ARRIVAL opens the
 * day at its destination; a same-day segment adds origin then destination;
 * a segment that departs today but lands tomorrow adds only its origin (the
 * destination belongs to the arrival day). Consecutive duplicates merge, so
 * a layover chain (ANR→AMS, AMS→LHR) reads Antwerp → Amsterdam → London.
 * Returns [{name, lat, lng}] — [] when nothing touches the date.
 */
export function buildTransportDayChain(transports, date) {
  if (!date) return [];
  const events = [];
  (transports || []).forEach(t => {
    if (!t?.departureLocal) return;
    const depDate = t.departureLocal.slice(0, 10);
    const arrDate = (t.arrivalLocal || t.departureLocal).slice(0, 10);
    const origin = {
      name: cityName(t.originCity, t.originName),
      lat: t.originCoordinates?.lat ?? null,
      lng: t.originCoordinates?.lng ?? null
    };
    const dest = {
      name: cityName(t.destinationCity, t.destinationName),
      lat: t.destinationCoordinates?.lat ?? null,
      lng: t.destinationCoordinates?.lng ?? null
    };
    if (depDate === date) {
      events.push({
        time: t.departureLocal.slice(11, 16) || '00:00',
        stops: arrDate === date ? [origin, dest] : [origin]
      });
    } else if (arrDate === date) {
      events.push({
        time: (t.arrivalLocal || '').slice(11, 16) || '00:00',
        stops: [dest]
      });
    }
  });
  events.sort((a, b) => a.time.localeCompare(b.time));
  const chain = [];
  events.forEach(e => e.stops.forEach(s => {
    if (!s.name) return;
    if (chain.length === 0 || chain[chain.length - 1].name.toLowerCase() !== s.name.toLowerCase()) {
      chain.push(s);
    }
  }));
  return chain;
}

/**
 * Short route label. FLIGHTS prefer codes ("LAX → SYD") — an IATA pair is the
 * idiomatic, unambiguous way to write a flight route. Every other mode prefers
 * the NAME ("Brussels Midi → Amsterdam Centraal"): ground codes are optional,
 * rarely known, and letting one win produced labels like "BRU → AMS" for a
 * city-centre train — airport codes standing in for stations that aren't
 * airports. The code stays as the fallback when no name was entered.
 */
export const transportRouteLabel = (t) => {
  const preferCode = t?.mode === 'flight';
  const end = (code, name) => (preferCode ? (code || name) : (name || code)) || '?';
  return `${end(t?.originCode, t?.originName)} → ${end(t?.destinationCode, t?.destinationName)}`;
};

/** "DL 89" from carrierCode + segmentNumber, falling back to carrier name. */
export const transportTitle = (t) => {
  const num = [t?.carrierCode, t?.segmentNumber].filter(Boolean).join(' ');
  if (num) return num;
  if (t?.carrierName) return t.carrierName;
  return transportModeMeta(t?.mode).label;
};
